import React, { useState, useEffect } from 'react';
import { createClient } from "@supabase/supabase-js";
import { useNavigate } from 'react-router-dom';
import AdminNavBar from '../../components/AdminNavBar';
import supabase from '../../utils/supabaseClient';
import { useSettings } from '../../contexts/SettingsContext';
import { setCatalogueMode } from '../../data/settings';

const translations = {
  es: {
    login: "Iniciar sesión",
    email: "Correo electrónico",
    password: "Contraseña",
    loginBtn: "Entrar",
    logout: "Cerrar sesión",
    loginError: "Error al iniciar sesión. Verifique sus datos.",
    welcome: "Bienvenido",
    catalogueMode: "Modo catálogo (ocultar precios · consultar por WhatsApp)",
    catalogueModeHint: "Cuando está activo, no se muestran precios en el sitio público y cada precio se reemplaza por una consulta por WhatsApp.",
    saving: "Guardando…",
    saved: "Guardado",
    saveError: "No se pudo guardar. Intente de nuevo."
  },
  en: {
    login: "Login",
    email: "Email",
    password: "Password",
    loginBtn: "Login",
    logout: "Logout",
    loginError: "Login failed. Please check your credentials.",
    welcome: "Welcome",
    catalogueMode: "Catalogue mode (hide prices · consult via WhatsApp)",
    catalogueModeHint: "When on, the public site shows no prices; each price is replaced by a WhatsApp consult link.",
    saving: "Saving…",
    saved: "Saved",
    saveError: "Could not save. Please try again."
  }
};

function Admin({ lang = 'es' }) {
  const t = translations[lang] || translations.es;
  
  const navigate = useNavigate(); // Use the navigate hook for redirection

  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(true);

  const { catalogueMode, refresh: refreshSettings } = useSettings();
  const [savingCatalogueMode, setSavingCatalogueMode] = useState(false);
  const [catalogueModeStatus, setCatalogueModeStatus] = useState(''); // '', 'saved', 'error'

  const handleCatalogueModeToggle = async (next) => {
    setSavingCatalogueMode(true);
    setCatalogueModeStatus('');
    try {
      await setCatalogueMode(next);
      await refreshSettings();
      setCatalogueModeStatus('saved');
    } catch (err) {
      console.error('Error saving catalogue mode:', err);
      setCatalogueModeStatus('error');
    } finally {
      setSavingCatalogueMode(false);
    }
  };

  useEffect(() => {
    // Get initial user state
    const getInitialUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      setLoading(false);
    };
    
    getInitialUser();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN') {
          setUser(session?.user || null);
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setLoading(false);
        }
      }
    );
    
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    const { error } = await supabase.auth.signInWithPassword({
      email: loginForm.email,
      password: loginForm.password
    });
    if (error) {
      setLoginError(t.loginError);
    }
    // Remove the immediate redirect - let the auth state change handle the UI update
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Cargando...</div>
      </div>
    );
  }

  return (
    <>
      <AdminNavBar user={user} />
      <div className="login-page" style={{ maxWidth: 340, margin: '40px auto', padding: 24, borderRadius: 8, background: '#f7f7f7' }}>
        <h2 style={{ textAlign: 'center', marginBottom: 24 }}>{t.login}</h2>
        {user ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 8 }}>{t.welcome}, {user.email}</div>
            <p style={{ marginTop: 20, color: '#666' }}>
              Use la barra de navegación para acceder a las secciones de administración.
            </p>

            <div style={{ marginTop: 24, padding: 20, borderRadius: 10, background: '#fff', border: '1px solid #e0e0e0', textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={catalogueMode}
                  aria-label={t.catalogueMode}
                  onClick={() => !savingCatalogueMode && handleCatalogueModeToggle(!catalogueMode)}
                  disabled={savingCatalogueMode}
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                    width: 96,
                    height: 48,
                    padding: 0,
                    border: 'none',
                    borderRadius: 999,
                    background: catalogueMode ? '#25D366' : '#c4c4c4',
                    cursor: savingCatalogueMode ? 'wait' : 'pointer',
                    transition: 'background 0.2s ease',
                    opacity: savingCatalogueMode ? 0.6 : 1,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 5,
                      left: catalogueMode ? 53 : 5,
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.35)',
                      transition: 'left 0.2s ease',
                    }}
                  />
                </button>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 17 }}>{t.catalogueMode}</div>
                  <div style={{ fontSize: 14, color: catalogueMode ? '#1b7a3d' : '#888', fontWeight: 600, marginTop: 2 }}>
                    {catalogueMode ? 'ON' : 'OFF'}
                  </div>
                </div>
              </div>
              <p style={{ margin: '12px 0 0', color: '#666', fontSize: 13 }}>{t.catalogueModeHint}</p>
              {savingCatalogueMode && <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>{t.saving}</div>}
              {!savingCatalogueMode && catalogueModeStatus === 'saved' && (
                <div style={{ marginTop: 8, fontSize: 13, color: '#2e7d32' }}>{t.saved}</div>
              )}
              {!savingCatalogueMode && catalogueModeStatus === 'error' && (
                <div style={{ marginTop: 8, fontSize: 13, color: '#e60026' }}>{t.saveError}</div>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label>
              {t.email}
              <input
                type="email"
                value={loginForm.email}
                onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
                required
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }}
              />
            </label>
            <label>
              {t.password}
              <input
                type="password"
                value={loginForm.password}
                onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                required
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }}
              />
            </label>
            {loginError && <div style={{ color: '#e60026', fontSize: 14 }}>{loginError}</div>}
            <button
              type="submit"
              style={{
                background: '#e60026',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '10px 0',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {t.loginBtn}
            </button>
          </form>
        )}
      </div>
    </>
  );
}

export default Admin;